import React, {ReactElement, useEffect, useRef} from 'react';
import {IElementEllipse} from '../../../../incase-domain/elements/shapes/ellipse';

interface EllipseProps {
    element: IElementEllipse;
}

function Ellipse({element}: EllipseProps): ReactElement {
    const {color = 'black', fill, isCircle, thickness = 0.5} = element;
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = React.useState(0);
    const [containerHeight, setContainerHeight] = React.useState(0);
    const [fontSizePx, setFontSizePx] = React.useState(16);

    const safeWidth = containerWidth > 0 ? containerWidth : 100;
    const safeHeight = containerHeight > 0 ? containerHeight : 100;
    const thicknessPx = fontSizePx * thickness;
    const rx = Math.max(0, (safeWidth - thicknessPx) / 2);
    const ry = Math.max(0, (safeHeight - thicknessPx) / 2);

    useEffect(() => {
        const observedElem = containerRef.current;
        const updateSize = (): void => {
            if (observedElem) {
                const rect = observedElem.getBoundingClientRect();
                setContainerWidth((prev) => (Math.abs(prev - rect.width) > 0.5 ? rect.width : prev));
                setContainerHeight((prev) => (Math.abs(prev - rect.height) > 0.5 ? rect.height : prev));
                const computedFontSize = window.getComputedStyle(observedElem).fontSize;
                setFontSizePx(parseFloat(computedFontSize));
            }
        };

        updateSize();

        let resizeObserver: ResizeObserver | null = null;
        if (observedElem && typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => updateSize());
            resizeObserver.observe(observedElem);
        }

        return () => {
            if (resizeObserver && observedElem) {
                resizeObserver.unobserve(observedElem);
            }
        };
    }, []);

    if (isCircle) {
        const r = Math.max(0, Math.min(rx, ry));
        return (
            <div ref={containerRef} className="c-circle">
                <svg width="100%" height="100%" viewBox={`0 0 ${safeWidth} ${safeHeight}`} xmlns="http://www.w3.org/2000/svg">
                    <circle
                        cx={safeWidth / 2}
                        cy={safeHeight / 2}
                        r={r}
                        fill={fill ? color : 'none'}
                        stroke={color}
                        strokeWidth={`${thickness}em`}
                    />
                </svg>
            </div>
        );
    }

    return (
        <div ref={containerRef} className="c-ellipse">
            <svg width="100%" height="100%" viewBox={`0 0 ${safeWidth} ${safeHeight}`} xmlns="http://www.w3.org/2000/svg">
                <ellipse
                    cx={safeWidth / 2}
                    cy={safeHeight / 2}
                    rx={rx}
                    ry={ry}
                    fill={fill ? color : 'none'}
                    stroke={color}
                    strokeWidth={`${thickness}em`}
                />
            </svg>
        </div>
    );
}

export default Ellipse;
